import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, X, Check, ChevronDown, ChevronUp, Facebook, Search, Mail, Linkedin } from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { Panel } from "@/components/Panel";
import { cn } from "@/lib/cn";
import {
  useMarketingActivities,
  useCreateActivity,
  useUpdateActivity,
  useDeleteActivity,
  useInstantlyCampaigns,
  type MarketingActivity,
  type ActivityCreate,
  type InstantlyCampaign,
} from "@/lib/api";
import { formatCurrency, formatDate, formatNumber, formatPercentRaw } from "@/lib/format";
import { usePayload } from "@/lib/usePayload";
import { getGoogleAdsCampaigns, getMetaCampaigns } from "@/lib/viewModeSelectors";

const SEGMENTS = [
  { key: "smb", label: "SMB" },
  { key: "mid_market", label: "Mid Market" },
  { key: "channels", label: "Channels" },
] as const;

type Segment = (typeof SEGMENTS)[number]["key"];

const ACTIVITY_TYPES = [
  "Campaign",
  "Content",
  "Event",
  "Outbound",
  "Partnership",
  "Email",
  "Webinar",
  "SEO / Organic",
  "Paid Ads",
  "Other",
];

const STATUS_META: Record<string, { label: string; className: string }> = {
  planned: { label: "Planned", className: "bg-slate-100 text-slate-600" },
  in_progress: { label: "In Progress", className: "bg-blue-100 text-blue-700" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700" },
  paused: { label: "Paused", className: "bg-amber-100 text-amber-700" },
};

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { label: status, className: "bg-slate-100 text-slate-600" };
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium", meta.className)}>
      {meta.label}
    </span>
  );
}

// ── Shared collapse toggle ───────────────────────────────────────────────────

function CollapseToggle({ open, onToggle, count }: { open: boolean; onToggle: () => void; count: number }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="coquette-toggle inline-flex items-center gap-1.5 text-xs font-medium transition-colors"
      style={{ color: '#f472b6' }}
    >
      {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      {open ? "Hide" : `Show ${count} campaigns`}
    </button>
  );
}

// ── Meta Ads campaign cards ─────────────────────────────────────────────────

function MetaActivityPanel({ payload, viewMode }: { payload: any; viewMode: string }) {
  const [showTable, setShowTable] = useState(false);
  const campaigns = getMetaCampaigns(payload, viewMode as any);
  const smb = campaigns.filter((c: any) => !((c.campaign_name || "").toLowerCase().includes("builder")));

  const totalSpend = smb.reduce((s: number, c: any) => s + (c.spend || 0), 0);
  const totalResults = smb.reduce((s: number, c: any) => s + (c.results || 0), 0);
  const totalImpressions = smb.reduce((s: number, c: any) => s + (c.impressions || 0), 0);
  const totalClicks = smb.reduce((s: number, c: any) => s + (c.clicks || 0), 0);
  const ctr = totalImpressions ? (totalClicks / totalImpressions) * 100 : null;
  const isCampaignActive = (c: any) =>
    (c.status || "").toUpperCase() === "ACTIVE" &&
    (c.effective_status || "").toUpperCase() === "ACTIVE";
  const activeCount = smb.filter(isCampaignActive).length;

  if (smb.length === 0) {
    return (
      <div className="text-sm text-slate-400 py-4 text-center">
        No Meta campaign data for this period.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: "Spend", value: formatCurrency(totalSpend, { compact: true }) },
          { label: "Results (Leads)", value: formatNumber(totalResults) },
          { label: "Clicks", value: formatNumber(totalClicks) },
          { label: "CTR", value: ctr != null ? formatPercentRaw(ctr / 100) : "—" },
        ].map(({ label, value }) => (
          <div key={label} className="bg-slate-50 border border-border rounded-card p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
            <div className="text-base font-bold text-ink-900 mt-0.5 numeric-value">{value}</div>
          </div>
        ))}
      </div>

      <CollapseToggle open={showTable} onToggle={() => setShowTable((v) => !v)} count={smb.length} />

      {/* Campaign rows */}
      {showTable && (
        <>
          <div className="overflow-x-auto rounded-card border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="label-xs border-b border-border bg-slate-50/60">
                  <th className="text-left font-medium px-3 py-2">Campaign</th>
                  <th className="font-medium px-3 py-2 text-center">Status</th>
                  <th className="font-medium px-3 py-2 text-right">Spend</th>
                  <th className="font-medium px-3 py-2 text-right">Results</th>
                  <th className="font-medium px-3 py-2 text-right">CPR</th>
                  <th className="font-medium px-3 py-2 text-right">Impressions</th>
                  <th className="font-medium px-3 py-2 text-right">CTR</th>
                </tr>
              </thead>
              <tbody>
                {smb.map((c: any, i: number) => {
                  const isActive = isCampaignActive(c);
                  const statusMismatch =
                    (c.status || "").toUpperCase() !== (c.effective_status || "").toUpperCase();
                  const displayStatus = isActive
                    ? "Active"
                    : (c.status || c.effective_status || "Paused");
                  const cpr = c.results && c.spend ? c.spend / c.results : null;
                  const campaignCtr = c.impressions ? (c.clicks / c.impressions) * 100 : null;
                  return (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-medium text-ink-900 max-w-[200px] truncate" title={c.campaign_name}>
                        {c.campaign_name || "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex flex-col items-center gap-0.5">
                          <span className={cn(
                            "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded",
                            isActive ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                          )}>
                            <span className={cn("h-1.5 w-1.5 rounded-full", isActive ? "bg-green-500" : "bg-slate-400")} />
                            {displayStatus}
                          </span>
                          {statusMismatch && (
                            <span className="text-[9px] text-amber-600" title={`Configured: ${c.status} / Effective: ${c.effective_status}`}>
                              ⚠ {c.status} / {c.effective_status}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right numeric-value">{formatCurrency(c.spend || 0)}</td>
                      <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.results || 0)}</td>
                      <td className="px-3 py-2 text-right numeric-value">{cpr != null ? formatCurrency(cpr) : "—"}</td>
                      <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.impressions || 0)}</td>
                      <td className="px-3 py-2 text-right numeric-value">
                        {campaignCtr != null ? formatPercentRaw(campaignCtr / 100) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-[11px] text-slate-400">
            {activeCount} of {smb.length} campaigns active · Excludes AI Website Builder campaigns
          </div>
        </>
      )}
    </div>
  );
}

// ── Google Ads campaign cards ───────────────────────────────────────────────

function GoogleAdsActivityPanel({ payload, viewMode }: { payload: any; viewMode: string }) {
  const [showTable, setShowTable] = useState(false);
  const campaigns = getGoogleAdsCampaigns(payload, viewMode as any);

  const totalCost = campaigns.reduce((s: number, c: any) => s + (c.cost || 0), 0);
  const totalClicks = campaigns.reduce((s: number, c: any) => s + (c.clicks || 0), 0);
  const totalImpressions = campaigns.reduce((s: number, c: any) => s + (c.impressions || 0), 0);
  const totalConversions = campaigns.reduce((s: number, c: any) => s + (c.conversions || 0), 0);
  const activeCount = campaigns.filter((c: any) => (c.status || "").toUpperCase() === "ENABLED").length;

  if (campaigns.length === 0) {
    return (
      <div className="text-sm text-slate-400 py-4 text-center">
        No Google Ads campaign data for this period.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: "Total Cost", value: formatCurrency(totalCost, { compact: true }) },
          { label: "Clicks", value: formatNumber(totalClicks) },
          { label: "Impressions", value: formatNumber(totalImpressions) },
          { label: "Conversions", value: formatNumber(totalConversions) },
        ].map(({ label, value }) => (
          <div key={label} className="bg-slate-50 border border-border rounded-card p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
            <div className="text-base font-bold text-ink-900 mt-0.5 numeric-value">{value}</div>
          </div>
        ))}
      </div>

      <CollapseToggle open={showTable} onToggle={() => setShowTable((v) => !v)} count={campaigns.length} />

      {/* Campaign rows */}
      {showTable && (
        <>
          <div className="overflow-x-auto rounded-card border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="label-xs border-b border-border bg-slate-50/60">
                  <th className="text-left font-medium px-3 py-2">Campaign</th>
                  <th className="font-medium px-3 py-2">Type</th>
                  <th className="font-medium px-3 py-2 text-center">Status</th>
                  <th className="font-medium px-3 py-2 text-right">Budget</th>
                  <th className="font-medium px-3 py-2 text-right">Cost</th>
                  <th className="font-medium px-3 py-2 text-right">Clicks</th>
                  <th className="font-medium px-3 py-2 text-right">CTR</th>
                  <th className="font-medium px-3 py-2 text-right">Avg CPC</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c: any, i: number) => {
                  const isEnabled = (c.status || "").toUpperCase() === "ENABLED";
                  const ctr = c.impressions ? (c.clicks / c.impressions) * 100 : null;
                  return (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-medium text-ink-900 max-w-[200px] truncate" title={c.campaign_name}>
                        {c.campaign_name || "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-[11px]">{c.campaign_type || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={cn(
                          "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded",
                          isEnabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                        )}>
                          <span className={cn("h-1.5 w-1.5 rounded-full", isEnabled ? "bg-green-500" : "bg-slate-400")} />
                          {isEnabled ? "Active" : (c.status || "Paused")}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right numeric-value">{c.budget ? formatCurrency(c.budget) : "—"}</td>
                      <td className="px-3 py-2 text-right numeric-value">{formatCurrency(c.cost || 0)}</td>
                      <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.clicks || 0)}</td>
                      <td className="px-3 py-2 text-right numeric-value">{ctr != null ? formatPercentRaw(ctr / 100) : "—"}</td>
                      <td className="px-3 py-2 text-right numeric-value">{c.avg_cpc ? formatCurrency(c.avg_cpc) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-[11px] text-slate-400">
            {activeCount} of {campaigns.length} campaigns active
          </div>
        </>
      )}
    </div>
  );
}

// ── LinkedIn Ads ─────────────────────────────────────────────────────────────
// Sep 2026 one-time test — LinkedIn was paused after this trial.
// Data is fixed here so it always shows regardless of selected month.

const LINKEDIN_HARDCODED_MONTH = "September 2026";

const LINKEDIN_HARDCODED_CAMPAIGNS = [
  {
    campaign_name: "Brand awareness - Aug 18, 2026",
    status: "ACTIVE",
    spend: 299,
    impressions: 2201,
    clicks: 7,
    conversions: 0,
    cpc: 43,
    ctr: 0.3,
  },
  {
    campaign_name: "ROI-Calculator-MatchedList-Sep26",
    status: "PAUSED",
    spend: 247,
    impressions: 505,
    clicks: 1,
    conversions: 0,
    cpc: 247,
    ctr: 0.2,
  },
  {
    campaign_name: "ROI-Calculator-MatchedList-Sep26-1.0",
    status: "PAUSED",
    spend: 443,
    impressions: 550,
    clicks: 5,
    conversions: 0,
    cpc: 89,
    ctr: 0.9,
  },
];

const LINKEDIN_HARDCODED_SUMMARY = {
  total_spend: LINKEDIN_HARDCODED_CAMPAIGNS.reduce((s, c) => s + c.spend, 0),
  total_clicks: LINKEDIN_HARDCODED_CAMPAIGNS.reduce((s, c) => s + c.clicks, 0),
  total_impressions: LINKEDIN_HARDCODED_CAMPAIGNS.reduce((s, c) => s + c.impressions, 0),
  total_conversions: LINKEDIN_HARDCODED_CAMPAIGNS.reduce((s, c) => s + c.conversions, 0),
};

function LinkedInActivityPanel({ payload: _ignored }: { payload: any }) {
  const [showTable, setShowTable] = useState(false);

  const campaigns = LINKEDIN_HARDCODED_CAMPAIGNS;
  const summary = LINKEDIN_HARDCODED_SUMMARY;

  const totalSpend = summary.total_spend ?? campaigns.reduce((s: number, c: any) => s + (c.spend || 0), 0);
  const totalClicks = summary.total_clicks ?? campaigns.reduce((s: number, c: any) => s + (c.clicks || 0), 0);
  const totalImpressions = summary.total_impressions ?? campaigns.reduce((s: number, c: any) => s + (c.impressions || 0), 0);
  const totalConversions = summary.total_conversions ?? campaigns.reduce((s: number, c: any) => s + (c.conversions || 0), 0);
  const activeCount = campaigns.filter((c: any) => (c.status || "").toUpperCase() === "ACTIVE").length;
  const ctr = totalImpressions ? (totalClicks / totalImpressions) * 100 : null;

  const sorted = [...campaigns].sort((a, b) => {
    const aActive = (a.status || "").toUpperCase() === "ACTIVE" ? 0 : 1;
    const bActive = (b.status || "").toUpperCase() === "ACTIVE" ? 0 : 1;
    return aActive - bActive;
  });

  return (
    <div className="space-y-3">
      {/* Test period notice */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
        <span className="text-base">📌</span>
        <span>
          <strong>Test period — {LINKEDIN_HARDCODED_MONTH}.</strong>{" "}
          LinkedIn was paused after this trial. This data is fixed to the test period regardless of the selected month.
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: "Spend", value: formatCurrency(totalSpend, { compact: true }) },
          { label: "Clicks", value: formatNumber(totalClicks) },
          { label: "Conversions", value: formatNumber(totalConversions) },
          { label: "CTR", value: ctr != null ? formatPercentRaw(ctr / 100) : "—" },
        ].map(({ label, value }) => (
          <div key={label} className="bg-slate-50 border border-border rounded-card p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
            <div className="text-base font-bold text-ink-900 mt-0.5 numeric-value">{value}</div>
          </div>
        ))}
      </div>

      <CollapseToggle open={showTable} onToggle={() => setShowTable((v) => !v)} count={campaigns.length} />

      {showTable && (
        <>
          <div className="overflow-x-auto rounded-card border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="label-xs border-b border-border bg-slate-50/60">
                  <th className="text-left font-medium px-3 py-2">Campaign</th>
                  <th className="font-medium px-3 py-2 text-center">Status</th>
                  <th className="font-medium px-3 py-2 text-right">Spend</th>
                  <th className="font-medium px-3 py-2 text-right">Impressions</th>
                  <th className="font-medium px-3 py-2 text-right">Clicks</th>
                  <th className="font-medium px-3 py-2 text-right">Conversions</th>
                  <th className="font-medium px-3 py-2 text-right">CPC</th>
                  <th className="font-medium px-3 py-2 text-right">CTR</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c: any, i: number) => {
                  const isActive = (c.status || "").toUpperCase() === "ACTIVE";
                  const campaignCtr = c.ctr ?? (c.impressions ? (c.clicks / c.impressions) * 100 : null);
                  return (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-medium text-ink-900 max-w-[200px] truncate" title={c.campaign_name}>
                        {c.campaign_name || "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={cn(
                          "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded",
                          isActive ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                        )}>
                          <span className={cn("h-1.5 w-1.5 rounded-full", isActive ? "bg-green-500" : "bg-slate-400")} />
                          {isActive ? "Active" : (c.status || "Paused")}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right numeric-value">{formatCurrency(c.spend || 0)}</td>
                      <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.impressions || 0)}</td>
                      <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.clicks || 0)}</td>
                      <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.conversions || 0)}</td>
                      <td className="px-3 py-2 text-right numeric-value">{c.cpc ? formatCurrency(c.cpc) : "—"}</td>
                      <td className="px-3 py-2 text-right numeric-value">
                        {campaignCtr != null ? formatPercentRaw(campaignCtr / 100) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-[11px] text-slate-400">
            {activeCount} of {campaigns.length} campaigns active · Test period: {LINKEDIN_HARDCODED_MONTH}
          </div>
        </>
      )}
    </div>
  );
}

// ── Instantly.ai email campaigns ────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  completed: "bg-blue-100 text-blue-700",
  paused: "bg-slate-100 text-slate-500",
  draft: "bg-amber-100 text-amber-700",
};

function pct(num: number, den: number) {
  if (!den) return "—";
  return `${((num / den) * 100).toFixed(1)}%`;
}

function EmailCampaignsPanel({ segment }: { segment: Segment }) {
  const [showTable, setShowTable] = useState(false);
  const { data: allCampaigns = [], isLoading, error } = useInstantlyCampaigns();
  const STATUS_ORDER: Record<string, number> = { active: 0, paused: 1, completed: 2, draft: 3 };
  const campaigns = allCampaigns
    .filter((c) => c.segment === segment)
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));

  const totalSent = campaigns.reduce((s, c) => s + c.emails_sent, 0);
  const totalOpens = campaigns.reduce((s, c) => s + c.open_count, 0);
  const totalReplies = campaigns.reduce((s, c) => s + c.reply_count, 0);
  const activeCount = campaigns.filter((c) => c.status === "active").length;

  if (isLoading) {
    return <div className="text-sm text-slate-400 py-4 text-center animate-pulse">Loading email campaigns…</div>;
  }

  if (error) {
    return (
      <div className="text-sm text-slate-400 py-4 text-center">
        Could not load Instantly data. Check that <code className="bg-slate-100 px-1 rounded text-xs">INSTANTLY_API_KEY</code> is set.
      </div>
    );
  }

  if (!isLoading && !error && allCampaigns.length > 0 && campaigns.length === 0) {
    return (
      <div className="text-sm text-slate-400 py-4 text-center">
        No email campaigns found for this segment.
      </div>
    );
  }

  if (!isLoading && !error && allCampaigns.length === 0) {
    return (
      <div className="text-sm text-slate-400 py-4 text-center">
        No campaigns found. Make sure <code className="bg-slate-100 px-1 rounded text-xs">INSTANTLY_API_KEY</code> is set in your <code className="bg-slate-100 px-1 rounded text-xs">.env</code>.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          { label: "Emails Sent", value: formatNumber(totalSent) },
          { label: "Opens", value: formatNumber(totalOpens) },
          { label: "Open Rate", value: pct(totalOpens, totalSent) },
          { label: "Replies", value: formatNumber(totalReplies) },
          { label: "Reply Rate", value: pct(totalReplies, totalSent) },
        ].map(({ label, value }) => (
          <div key={label} className="bg-slate-50 border border-border rounded-card p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
            <div className="text-base font-bold text-ink-900 mt-0.5 numeric-value">{value}</div>
          </div>
        ))}
      </div>

      <CollapseToggle open={showTable} onToggle={() => setShowTable((v) => !v)} count={campaigns.length} />

      {/* Campaign table */}
      {showTable && (
        <>
          <div className="overflow-x-auto rounded-card border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="label-xs border-b border-border bg-slate-50/60">
                  <th className="text-left font-medium px-3 py-2">Campaign</th>
                  <th className="font-medium px-3 py-2 text-center">Status</th>
                  <th className="font-medium px-3 py-2 text-right">Leads</th>
                  <th className="font-medium px-3 py-2 text-right">Contacted</th>
                  <th className="font-medium px-3 py-2 text-right">Sent</th>
                  <th className="font-medium px-3 py-2 text-right">Opens</th>
                  <th className="font-medium px-3 py-2 text-right">Open %</th>
                  <th className="font-medium px-3 py-2 text-right">Replies</th>
                  <th className="font-medium px-3 py-2 text-right">Reply %</th>
                  <th className="font-medium px-3 py-2 text-right">Bounced</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c: InstantlyCampaign) => (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-ink-900 max-w-[220px] truncate" title={c.name}>
                      {c.name}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={cn(
                        "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded capitalize",
                        STATUS_COLOR[c.status] ?? "bg-slate-100 text-slate-500"
                      )}>
                        <span className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          c.status === "active" ? "bg-green-500" :
                          c.status === "completed" ? "bg-blue-500" :
                          "bg-slate-400"
                        )} />
                        {c.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.leads_count)}</td>
                    <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.contacted)}</td>
                    <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.emails_sent)}</td>
                    <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.open_count)}</td>
                    <td className="px-3 py-2 text-right numeric-value text-slate-500">{pct(c.open_count, c.emails_sent)}</td>
                    <td className="px-3 py-2 text-right numeric-value">{formatNumber(c.reply_count)}</td>
                    <td className="px-3 py-2 text-right numeric-value text-slate-500">{pct(c.reply_count, c.emails_sent)}</td>
                    <td className="px-3 py-2 text-right numeric-value text-rose-600">{formatNumber(c.bounced_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-[11px] text-slate-400">
            {activeCount} active · Analytics over last 90 days · Data from Instantly.ai
          </div>
        </>
      )}
    </div>
  );
}

// ── Manual activity form & cards ────────────────────────────────────────────

interface FormState {
  title: string;
  description: string;
  activity_type: string;
  status: string;
  activity_date: string;
}

const EMPTY_FORM: FormState = {
  title: "",
  description: "",
  activity_type: "",
  status: "planned",
  activity_date: "",
};

function ActivityForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial?: FormState;
  onSave: (data: FormState) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<FormState>(initial ?? EMPTY_FORM);
  const set = (k: keyof FormState, v: string) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="border border-blue-200 rounded-card bg-blue-50/40 p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-slate-700 mb-1">Title *</label>
          <input
            className="w-full text-sm border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/40 bg-white"
            placeholder="Activity title…"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Type</label>
          <div className="relative">
            <select
              className="w-full text-sm border border-border rounded-md px-3 py-2 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 bg-white pr-8"
              value={form.activity_type}
              onChange={(e) => set("activity_type", e.target.value)}
            >
              <option value="">— Select type —</option>
              {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-slate-400" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
          <div className="relative">
            <select
              className="w-full text-sm border border-border rounded-md px-3 py-2 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 bg-white pr-8"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
            >
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-slate-400" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Date</label>
          <input
            type="date"
            className="w-full text-sm border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/40 bg-white"
            value={form.activity_date}
            onChange={(e) => set("activity_date", e.target.value)}
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-slate-700 mb-1">Description</label>
          <textarea
            rows={3}
            className="w-full text-sm border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/40 bg-white resize-none"
            placeholder="Details, links, notes…"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={!form.title.trim() || saving}
          onClick={() => onSave(form)}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 disabled:opacity-50 text-white text-xs font-semibold rounded-md transition-colors"
          style={{ background: 'linear-gradient(135deg,#f472b6,#db2777)' }}
        >
          <Check className="h-3.5 w-3.5" />
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 border border-border text-slate-600 text-xs font-semibold rounded-md hover:bg-slate-50 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
          Cancel
        </button>
      </div>
    </div>
  );
}

function ActivityCard({
  activity,
  onEdit,
  onDelete,
}: {
  activity: MarketingActivity;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="border border-border rounded-card bg-white p-4 flex flex-col gap-2 hover:shadow-sm transition-shadow group">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-ink-900 leading-tight">{activity.title}</span>
            <StatusBadge status={activity.status} />
            {activity.activity_type && (
              <span className="text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                {activity.activity_type}
              </span>
            )}
          </div>
          {activity.activity_date && (
            <div className="text-[11px] text-slate-400 mt-0.5">{formatDate(activity.activity_date)}</div>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {activity.description && (
        <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">{activity.description}</p>
      )}
    </div>
  );
}

// ── Segment panel ───────────────────────────────────────────────────────────

function ManualActivitiesSection({ segment }: { segment: Segment }) {
  const { data: activities = [], isLoading } = useMarketingActivities(segment);
  const create = useCreateActivity();
  const update = useUpdateActivity();
  const del = useDeleteActivity();

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const handleCreate = async (form: FormState) => {
    const body: ActivityCreate = {
      segment,
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      activity_type: form.activity_type || undefined,
      status: form.status,
      activity_date: form.activity_date || undefined,
    };
    await create.mutateAsync(body);
    setAdding(false);
  };

  const handleUpdate = async (id: number, form: FormState) => {
    await update.mutateAsync({
      id,
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      activity_type: form.activity_type || undefined,
      status: form.status,
      activity_date: form.activity_date || undefined,
    });
    setEditingId(null);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this activity?")) return;
    await del.mutateAsync(id);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Other Activities & Initiatives
        </div>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-md transition-colors"
          style={{ background: 'linear-gradient(135deg,#f472b6,#db2777)' }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Activity
          </button>
        )}
      </div>

      {adding && (
        <ActivityForm
          onSave={handleCreate}
          onCancel={() => setAdding(false)}
          saving={create.isPending}
        />
      )}

      {isLoading && (
        <div className="text-sm text-slate-400 py-4 text-center animate-pulse">Loading…</div>
      )}

      {!isLoading && activities.length === 0 && !adding && (
        <div className="text-sm text-slate-400 py-4 text-center">
          No activities logged yet.{" "}
          <button
            type="button"
            className="hover:underline font-medium" style={{ color: '#db2777' }}
            onClick={() => setAdding(true)}
          >
            Add the first one
          </button>
        </div>
      )}

      {activities.map((a) =>
        editingId === a.id ? (
          <ActivityForm
            key={a.id}
            initial={{
              title: a.title,
              description: a.description ?? "",
              activity_type: a.activity_type ?? "",
              status: a.status,
              activity_date: a.activity_date ?? "",
            }}
            onSave={(form) => handleUpdate(a.id, form)}
            onCancel={() => setEditingId(null)}
            saving={update.isPending}
          />
        ) : (
          <ActivityCard
            key={a.id}
            activity={a}
            onEdit={() => setEditingId(a.id)}
            onDelete={() => handleDelete(a.id)}
          />
        )
      )}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

const COQUETTE_STYLE = `
/* ── keyframes ── */
@keyframes berry-float {
  0%,100% { transform: translateY(0) rotate(-8deg) scale(1); }
  33%      { transform: translateY(-16px) rotate(6deg) scale(1.12); }
  66%      { transform: translateY(-6px) rotate(-4deg) scale(0.94); }
}
@keyframes bow-sway {
  0%,100% { transform: rotate(-7deg) scale(1); }
  50%     { transform: rotate(7deg) scale(1.1); }
}
@keyframes sparkle-pop {
  0%,100% { opacity:0; transform: scale(0) rotate(0deg); }
  50%     { opacity:1; transform: scale(1) rotate(180deg); }
}
@keyframes cq-slide-up {
  from { opacity:0; transform: translateY(14px); }
  to   { opacity:1; transform: translateY(0); }
}
@keyframes heart-rise {
  0%   { opacity:1; transform: translateY(0) scale(1) rotate(-10deg); }
  100% { opacity:0; transform: translateY(-55px) scale(0.4) rotate(15deg); }
}
@keyframes petal-fall {
  0%   { opacity:0; transform: translateY(-30px) rotateZ(0deg) rotateX(0deg); }
  8%   { opacity:1; }
  100% { opacity:0; transform: translateY(110vh) rotateZ(720deg) rotateX(360deg); }
}
@keyframes title-shimmer {
  0%   { background-position: -400% center; }
  100% { background-position: 400% center; }
}
@keyframes tile-pop {
  0%   { opacity:0; transform: scale(0.8) translateY(12px); }
  65%  { transform: scale(1.05) translateY(-3px); }
  100% { opacity:1; transform: scale(1) translateY(0); }
}
@keyframes row-in {
  from { opacity:0; transform: translateX(-10px); }
  to   { opacity:1; transform: translateX(0); }
}
@keyframes glow-pulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(244,114,182,0); }
  50%      { box-shadow: 0 0 14px 5px rgba(244,114,182,.35); }
}
@keyframes tab-bob {
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-3px); }
}
@keyframes ribbon-wave {
  0%,100% { transform: scaleX(1) skewX(0deg); }
  25%      { transform: scaleX(1.01) skewX(1deg); }
  75%      { transform: scaleX(0.99) skewX(-1deg); }
}
@keyframes bg-drift {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes sparkle-cursor {
  0%   { opacity:1; transform: translate(-50%,-50%) scale(1.2) rotate(0deg); }
  60%  { opacity:.8; }
  100% { opacity:0; transform: translate(-50%, calc(-50% - 36px)) scale(0.2) rotate(200deg); }
}
@keyframes glitter-twinkle {
  0%,100% { opacity:0; transform: scale(0) rotate(0deg); }
  30%,70% { opacity:1; transform: scale(1) rotate(180deg); }
}
@keyframes glitter-pulse {
  0%,100% { opacity:.15; transform: scale(.8); }
  50%      { opacity:1;   transform: scale(1.3); }
}

.coquette-page {
  position: relative;
  border-radius: 12px;
  animation: cq-slide-up .35s ease-out;
}

/* Animated gradient background */
.coquette-page::before {
  content: '';
  position: fixed;
  inset: 0;
  background: linear-gradient(300deg, #fff0f6, #fce7f3, #ffe4ef, #fdf2f8, #fff0f6);
  background-size: 300% 300%;
  animation: bg-drift 12s ease infinite;
  z-index: -1;
  pointer-events: none;
}

/* Top ribbon banner */
.coquette-page::after {
  content: '🎀 ✨ 🍓 ✨ 🎀 ✨ 🍓 ✨ 🎀 ✨ 🍓 ✨ 🎀 ✨ 🍓 ✨ 🎀';
  position: sticky;
  top: 0;
  display: block;
  width: 100%;
  padding: 3px 0;
  text-align: center;
  font-size: 10px;
  letter-spacing: 6px;
  background: linear-gradient(90deg, #fbcfe8, #f9a8d4, #fbcfe8);
  background-size: 200% auto;
  animation: ribbon-wave 4s ease-in-out infinite, bg-drift 6s ease infinite;
  border-bottom: 1px solid #f9a8d4;
  border-radius: 8px 8px 0 0;
  z-index: 10;
  pointer-events: none;
  opacity: .85;
}

/* Panel cards */
.coquette-page .bg-white { background: linear-gradient(135deg,#fff8fb,#fdf2f8) !important; }
.coquette-page .border-border { border-color: #fbcfe8 !important; }
.coquette-page [class*="border-border"] { border-color: #fbcfe8 !important; }

/* KPI tiles */
.coquette-page .bg-slate-50 {
  background: linear-gradient(135deg,#fff0f6,#fce7f3) !important;
  border-color: #fbcfe8 !important;
}

/* Table header */
.coquette-page thead tr { background: linear-gradient(90deg,#fce7f3,#fff0f6) !important; }
.coquette-page th { color: #be185d !important; }

/* Table row hover */
.coquette-page tbody tr:hover { background-color: #fff0f6 !important; }
.coquette-page .hover\\:bg-slate-50\\/60:hover { background-color: #fff0f6 !important; }

/* Active green → hot pink */
.coquette-page .bg-green-100 { background-color: #fdf2f8 !important; }
.coquette-page .text-green-700 { color: #be185d !important; }
.coquette-page .bg-green-500 { background-color: #f472b6 !important; }

/* Blue → pink */
.coquette-page .bg-blue-100 { background-color: #fce7f3 !important; }
.coquette-page .text-blue-700 { color: #be185d !important; }
.coquette-page .text-blue-500 { color: #ec4899 !important; }
.coquette-page .bg-blue-500 { background-color: #ec4899 !important; }
.coquette-page .border-blue-200 { border-color: #fbcfe8 !important; }
.coquette-page .bg-blue-50\\/40 { background-color: rgba(253,242,248,.6) !important; }

/* Slate muted → pink muted */
.coquette-page .bg-slate-100 { background-color: #fdf2f8 !important; }
.coquette-page .text-slate-400 { color: #f9a8d4 !important; }
.coquette-page .text-slate-500 { color: #db2777 !important; }
.coquette-page .text-slate-600 { color: #be185d !important; }
.coquette-page .text-slate-700 { color: #9d174d !important; }
.coquette-page .text-ink-900  { color: #831843 !important; }

/* Amber/warning → keep readable */
.coquette-page .text-amber-600 { color: #d97706 !important; }
.coquette-page .bg-amber-100 { background-color: #fef3c7 !important; }

/* Form focus rings → pink */
.coquette-page input:focus,
.coquette-page textarea:focus,
.coquette-page select:focus { outline: none; box-shadow: 0 0 0 2px rgba(244,114,182,.4) !important; border-color: #f9a8d4 !important; }

/* Collapse toggle */
.coquette-page button.coquette-toggle { color: #f472b6 !important; }
.coquette-page button.coquette-toggle:hover { color: #be185d !important; }

/* Page title shimmer */
.coquette-title {
  background: linear-gradient(90deg, #9d174d, #f472b6, #db2777, #f9a8d4, #9d174d);
  background-size: 400% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: title-shimmer 4s linear infinite;
}

/* KPI tiles — pop in with stagger */
.coquette-page .grid > div:nth-child(1) { animation: tile-pop .35s ease-out .05s both; }
.coquette-page .grid > div:nth-child(2) { animation: tile-pop .35s ease-out .12s both; }
.coquette-page .grid > div:nth-child(3) { animation: tile-pop .35s ease-out .19s both; }
.coquette-page .grid > div:nth-child(4) { animation: tile-pop .35s ease-out .26s both; }
.coquette-page .grid > div:nth-child(5) { animation: tile-pop .35s ease-out .33s both; }

/* Table rows slide in */
.coquette-page tbody tr:nth-child(1)  { animation: row-in .25s ease-out .04s both; }
.coquette-page tbody tr:nth-child(2)  { animation: row-in .25s ease-out .08s both; }
.coquette-page tbody tr:nth-child(3)  { animation: row-in .25s ease-out .12s both; }
.coquette-page tbody tr:nth-child(4)  { animation: row-in .25s ease-out .16s both; }
.coquette-page tbody tr:nth-child(5)  { animation: row-in .25s ease-out .20s both; }
.coquette-page tbody tr:nth-child(6)  { animation: row-in .25s ease-out .24s both; }
.coquette-page tbody tr:nth-child(7)  { animation: row-in .25s ease-out .28s both; }
.coquette-page tbody tr:nth-child(8)  { animation: row-in .25s ease-out .32s both; }
.coquette-page tbody tr:nth-child(9)  { animation: row-in .25s ease-out .36s both; }
.coquette-page tbody tr:nth-child(10) { animation: row-in .25s ease-out .40s both; }

/* Active tab — glowing underline pulse */
.coquette-page button[class*="border-pink-500"] {
  animation: glow-pulse 2.4s ease-in-out infinite;
  border-radius: 6px 6px 0 0;
}

/* Buttons — bounce on hover + glow */
.coquette-page button[style*="gradient"] {
  transition: transform .15s ease, box-shadow .15s ease !important;
}
.coquette-page button[style*="gradient"]:hover {
  transform: translateY(-2px) scale(1.03) !important;
  box-shadow: 0 6px 20px rgba(244,114,182,.45) !important;
}
.coquette-page button[style*="gradient"]:active {
  transform: scale(.97) translateY(0) !important;
}

/* Panel entry animation */
.coquette-page .space-y-4 > * {
  animation: cq-slide-up .3s ease-out both;
}
.coquette-page .space-y-4 > *:nth-child(1) { animation-delay: .05s; }
.coquette-page .space-y-4 > *:nth-child(2) { animation-delay: .12s; }
.coquette-page .space-y-4 > *:nth-child(3) { animation-delay: .19s; }
.coquette-page .space-y-4 > *:nth-child(4) { animation-delay: .26s; }

/* ── Sidebar coquette overrides (body-scoped, outside .coquette-page) ── */
body.coquette-active aside {
  background: linear-gradient(160deg, #fff0f6 0%, #fce7f3 55%, #fff5f9 100%) !important;
  transition: background .5s ease;
}

/* Logo */
body.coquette-active aside > div:first-child {
  border-color: rgba(244,114,182,.25) !important;
}
body.coquette-active aside > div:first-child .font-display {
  background: linear-gradient(90deg, #9d174d, #f472b6, #db2777, #9d174d);
  background-size: 300% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: title-shimmer 5s linear infinite;
}
body.coquette-active aside > div:first-child .text-xs {
  color: #f472b6 !important;
}

/* Section labels (REPORTING / PRODUCT) */
body.coquette-active aside nav .text-\[10px\] {
  color: #f9a8d4 !important;
}

/* All nav links — high-contrast dark rose so text is readable */
body.coquette-active aside nav a {
  color: #9d174d !important;
}
body.coquette-active aside nav a:hover {
  background-color: rgba(244,114,182,.14) !important;
  color: #be185d !important;
}

/* Active nav link → pink gradient */
body.coquette-active aside nav a.bg-accent-blue,
body.coquette-active aside nav a[class*="bg-accent-blue"] {
  background: linear-gradient(135deg, #f472b6, #db2777) !important;
  color: #fff !important;
  box-shadow: 0 2px 10px rgba(244,114,182,.4) !important;
}

/* Badge chips */
body.coquette-active aside nav span.text-\[10px\] {
  background-color: rgba(244,114,182,.2) !important;
  color: #be185d !important;
}

/* Footer bar */
body.coquette-active aside > div:last-child {
  border-color: rgba(244,114,182,.2) !important;
  color: #9d174d !important;
}
body.coquette-active aside > div:last-child .truncate {
  color: #9d174d !important;
}
body.coquette-active aside > div:last-child .text-sidebar-muted,
body.coquette-active aside > div:last-child a {
  color: #f9a8d4 !important;
}
body.coquette-active aside > div:last-child a:hover {
  color: #db2777 !important;
}
`;

// ── Glitter overlay — static twinkling sparkles scattered across the page ───

const GLITTER_DOTS = Array.from({ length: 35 }, (_, i) => ({
  id: i,
  left: `${(i * 37 + 7) % 97}%`,
  top: `${(i * 53 + 11) % 93}%`,
  icon: ['✦', '✧', '✺', '⋆', '✵', '✴', '❋', '·'][i % 8],
  color: ['#f472b6','#fb7185','#f9a8d4','#e879f9','#c084fc','#fde68a','#fbcfe8','#fca5a5'][i % 8],
  fontSize: `${0.55 + (i % 5) * 0.15}rem`,
  duration: `${1.4 + (i % 7) * 0.4}s`,
  delay: `${(i * 0.23) % 3.5}s`,
}));

function GlitterOverlay() {
  return (
    <div className="pointer-events-none select-none" aria-hidden="true"
      style={{ position:'fixed', top:0, bottom:0, left:'240px', right:0, zIndex:1, overflow:'hidden' }}>
      {GLITTER_DOTS.map((d) => (
        <span key={d.id} style={{
          position:'absolute',
          left: d.left,
          top: d.top,
          fontSize: d.fontSize,
          color: d.color,
          animation: `glitter-twinkle ${d.duration} ease-in-out infinite`,
          animationDelay: d.delay,
          filter: 'drop-shadow(0 0 3px currentColor)',
        }}>{d.icon}</span>
      ))}
    </div>
  );
}

// ── Cursor sparkle trail ─────────────────────────────────────────────────────

interface CursorSparkle { id: number; x: number; y: number; icon: string; color: string }

const SPARKLE_ICONS = ['✦','✧','✺','⋆','✵','★','✨','💗','🌸'];
const SPARKLE_COLORS = ['#f472b6','#fb7185','#f9a8d4','#e879f9','#c084fc','#fde68a'];

function CursorSparkles() {
  const [sparks, setSparks] = useState<CursorSparkle[]>([]);
  const counterRef = useRef(0);
  const lastRef = useRef(0);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastRef.current < 45) return; // throttle ~22fps
      lastRef.current = now;
      const id = counterRef.current++;
      const spark: CursorSparkle = {
        id,
        x: e.clientX + (Math.random() - 0.5) * 18,
        y: e.clientY + (Math.random() - 0.5) * 18,
        icon: SPARKLE_ICONS[id % SPARKLE_ICONS.length],
        color: SPARKLE_COLORS[id % SPARKLE_COLORS.length],
      };
      setSparks((prev) => [...prev.slice(-20), spark]);
      setTimeout(() => setSparks((prev) => prev.filter((s) => s.id !== id)), 750);
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  return (
    <div className="pointer-events-none select-none" aria-hidden="true"
      style={{ position:'fixed', inset:0, zIndex:9999, overflow:'hidden' }}>
      {sparks.map((s) => (
        <span key={s.id} style={{
          position:'fixed',
          left: s.x,
          top: s.y,
          fontSize: '0.85rem',
          color: s.color,
          filter: 'drop-shadow(0 0 4px currentColor)',
          animation: 'sparkle-cursor .75s ease-out forwards',
          pointerEvents: 'none',
          willChange: 'transform, opacity',
        }}>{s.icon}</span>
      ))}
    </div>
  );
}

const SEGMENT_ICONS: Record<string, string> = {
  smb: "🍓",
  mid_market: "🎀",
  channels: "🍓",
};

export function MarketingUpdates() {
  const { payload, isLoading, monthLabel, viewMode } = usePayload();
  const [activeSegment, setActiveSegment] = useState<Segment>("smb");
  const [coquette, setCoquette] = useState<boolean>(() => {
    try { return localStorage.getItem('marketing-theme') === 'coquette'; }
    catch { return true; }
  });

  const toggleTheme = () => {
    const next = !coquette;
    setCoquette(next);
    try { localStorage.setItem('marketing-theme', next ? 'coquette' : 'default'); } catch {}
  };

  // Sync body class so sidebar CSS can react outside this component tree
  useEffect(() => {
    if (coquette) {
      document.body.classList.add('coquette-active');
    } else {
      document.body.classList.remove('coquette-active');
    }
    return () => document.body.classList.remove('coquette-active');
  }, [coquette]);

  if (!payload) return <EmptyState title="No data yet for this period" loading={isLoading} />;

  return (
    <div className={cn("space-y-4 sm:space-y-6", coquette && "coquette-page")}>
      {/* Coquette layer — only when theme is active */}
      {coquette && (
        <>
          <style dangerouslySetInnerHTML={{ __html: COQUETTE_STYLE }} />
          <GlitterOverlay />
          <CursorSparkles />
        </>
      )}

      {/* Floating + falling decorations — coquette only */}
      {coquette && <div className="pointer-events-none select-none" aria-hidden="true">
        {/* Right-edge floaters */}
        <div style={{ position:'fixed', top:0, right:0, bottom:0, width:'80px', zIndex:0 }}>
          <span style={{ position:'absolute', top:'18%', right:'28px', fontSize:'2rem',   animation:'berry-float 4s ease-in-out infinite',  animationDelay:'0s' }}>🍓</span>
          <span style={{ position:'absolute', top:'32%', right:'10px', fontSize:'1.5rem', animation:'bow-sway 3.5s ease-in-out infinite',    animationDelay:'0.6s' }}>🎀</span>
          <span style={{ position:'absolute', top:'52%', right:'24px', fontSize:'1.2rem', animation:'sparkle-pop 2.5s ease-in-out infinite', animationDelay:'0.3s' }}>✨</span>
          <span style={{ position:'absolute', top:'65%', right:'8px',  fontSize:'1.8rem', animation:'berry-float 5s ease-in-out infinite',   animationDelay:'1.2s' }}>🍓</span>
          <span style={{ position:'absolute', top:'78%', right:'32px', fontSize:'1.3rem', animation:'bow-sway 4s ease-in-out infinite',      animationDelay:'0.9s' }}>🎀</span>
          <span style={{ position:'absolute', top:'45%', right:'50px', fontSize:'1rem',   animation:'sparkle-pop 3s ease-in-out infinite',   animationDelay:'1.8s' }}>✨</span>
          <span style={{ position:'absolute', top:'25%', right:'60px', fontSize:'1.1rem', animation:'heart-rise 3s ease-in-out infinite',    animationDelay:'0.4s' }}>💗</span>
          <span style={{ position:'absolute', top:'58%', right:'42px', fontSize:'1.1rem', animation:'heart-rise 4s ease-in-out infinite',    animationDelay:'2.1s' }}>💗</span>
          <span style={{ position:'absolute', top:'12%', right:'16px', fontSize:'1.4rem', animation:'berry-float 6s ease-in-out infinite',   animationDelay:'2.5s' }}>🌸</span>
          <span style={{ position:'absolute', top:'70%', right:'55px', fontSize:'1rem',   animation:'bow-sway 5s ease-in-out infinite',      animationDelay:'3.2s' }}>🌸</span>
        </div>
        {/* Falling petals — start after sidebar (calc from 240px) */}
        {([
          { offset:'3%',  delay:'0s',   dur:'7s',  size:'1.1rem', icon:'🌸' },
          { offset:'12%', delay:'1.4s', dur:'8s',  size:'0.9rem', icon:'✨' },
          { offset:'22%', delay:'2.8s', dur:'6s',  size:'1.3rem', icon:'🍓' },
          { offset:'34%', delay:'0.7s', dur:'9s',  size:'0.8rem', icon:'💗' },
          { offset:'46%', delay:'3.5s', dur:'7.5s',size:'1rem',   icon:'🌸' },
          { offset:'58%', delay:'1.1s', dur:'8.5s',size:'1.2rem', icon:'✨' },
          { offset:'72%', delay:'4.2s', dur:'6.5s',size:'0.9rem', icon:'🎀' },
          { offset:'85%', delay:'2.0s', dur:'10s', size:'1rem',   icon:'🍓' },
        ] as const).map(({ offset, delay, dur, size, icon }, i) => (
          <span key={i} style={{
            position:'fixed', top:'-20px',
            left: `calc(240px + (100vw - 240px) * ${parseFloat(offset) / 100})`,
            fontSize: size,
            animation: `petal-fall ${dur} linear infinite`,
            animationDelay: delay,
            zIndex: 0,
          }}>{icon}</span>
        ))}
      </div>}

      {/* Page header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          {coquette ? (
            <h1 className="coquette-title font-display text-xl sm:text-2xl font-semibold">
              🎀 Marketing Updates
            </h1>
          ) : (
            <h1 className="font-display text-xl sm:text-2xl font-semibold text-ink-900">
              Marketing Updates
            </h1>
          )}
          <p className="text-xs sm:text-sm mt-0.5" style={coquette ? { color: '#f472b6' } : undefined}>
            Marketing activities and campaign performance — {monthLabel}
          </p>
        </div>

        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          title={coquette ? 'Switch to default theme' : 'Switch to coquette theme'}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all duration-300"
          style={coquette ? {
            background: 'linear-gradient(135deg,#fce7f3,#f9a8d4)',
            borderColor: '#f472b6',
            color: '#be185d',
            boxShadow: '0 2px 8px rgba(244,114,182,.3)',
          } : {
            background: 'white',
            borderColor: '#e2e8f0',
            color: '#64748b',
          }}
        >
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>{coquette ? '🎀' : '📊'}</span>
          {coquette ? 'Coquette' : 'Default'}
          {/* pill track */}
          <span className="relative inline-flex h-4 w-7 rounded-full transition-colors duration-300"
            style={{ background: coquette ? '#f472b6' : '#cbd5e1' }}>
            <span className="absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform duration-300"
              style={{ transform: coquette ? 'translateX(14px)' : 'translateX(2px)' }} />
          </span>
        </button>
      </div>

      {/* Segment tabs */}
      <div className="flex gap-1" style={coquette ? { borderBottom: '2px solid #fbcfe8' } : { borderBottom: '1px solid var(--color-border)' }}>
        {SEGMENTS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setActiveSegment(s.key)}
            className={cn(
              "px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px",
              coquette
                ? activeSegment === s.key
                  ? "border-pink-500 text-pink-600"
                  : "border-transparent hover:border-pink-300"
                : activeSegment === s.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            )}
            style={coquette && activeSegment !== s.key ? { color: '#f9a8d4' } : {}}
          >
            {coquette ? `${SEGMENT_ICONS[s.key]} ` : ''}{s.label}
          </button>
        ))}
      </div>

      {/* SMB — Meta, Google Ads, Email, Manual */}
      {activeSegment === "smb" && (
        <div className="space-y-4">
          <Panel
            title={<span className="inline-flex items-center gap-2"><Facebook className="h-4 w-4" style={{color: coquette ? '#f472b6' : '#2563eb'}} />Paid Social — Meta Ads</span>}
            subtitle="Facebook & Instagram campaign performance this month"
          >
            <MetaActivityPanel payload={payload} viewMode={viewMode} />
          </Panel>

          <Panel
            title={<span className="inline-flex items-center gap-2"><Search className="h-4 w-4" style={{color: coquette ? '#f472b6' : '#16a34a'}} />Paid Search — Google Ads</span>}
            subtitle="Search campaign performance this month"
          >
            <GoogleAdsActivityPanel payload={payload} viewMode={viewMode} />
          </Panel>

          <Panel
            title={<span className="inline-flex items-center gap-2"><Mail className="h-4 w-4" style={{color: coquette ? '#f472b6' : '#4f46e5'}} />Email Outreach — Instantly.ai</span>}
            subtitle="SMB email campaigns · lifetime analytics"
          >
            <EmailCampaignsPanel segment="smb" />
          </Panel>

          <Panel title={coquette ? "🍓 Other SMB Activities" : "Other SMB Activities"}>
            <ManualActivitiesSection segment="smb" />
          </Panel>
        </div>
      )}

      {/* Mid Market — LinkedIn + Email + Manual */}
      {activeSegment === "mid_market" && (
        <div className="space-y-4">
          <Panel
            title={<span className="inline-flex items-center gap-2"><Linkedin className="h-4 w-4" style={{color: coquette ? '#f472b6' : '#0284c7'}} />LinkedIn Ads</span>}
            subtitle="Sponsored content & lead gen performance this month"
          >
            <LinkedInActivityPanel payload={payload} />
          </Panel>

          <Panel
            title={<span className="inline-flex items-center gap-2"><Mail className="h-4 w-4" style={{color: coquette ? '#f472b6' : '#4f46e5'}} />Email Outreach — Instantly.ai</span>}
            subtitle="Mid Market email campaigns · lifetime analytics"
          >
            <EmailCampaignsPanel segment="mid_market" />
          </Panel>

          <Panel title={coquette ? "🎀 Other Mid Market Activities" : "Other Mid Market Activities"}>
            {/* Hardcoded pinned activities — always visible regardless of month */}
            <div className="border border-slate-200 rounded-card bg-slate-50/60 p-4 flex flex-col gap-2 mb-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base">📌</span>
                    <span className="font-semibold text-sm text-ink-900 leading-tight">
                      Mid Market–Only Meta Campaigns
                    </span>
                    <span className="text-[11px] font-medium bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">Stopped</span>
                    <span className="text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Meta Ads</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Ran separate Meta campaigns targeting Mid Market audiences only. Performance was poor — low conversion rates and high CPL — so these were discontinued.
              </p>
            </div>
            <div className="border border-green-200 rounded-card bg-green-50/60 p-4 flex flex-col gap-2 mb-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base">📌</span>
                    <span className="font-semibold text-sm text-ink-900 leading-tight">
                      Meta Campaigns — SMB &amp; Mid Market (Combined)
                    </span>
                    <span className="text-[11px] font-medium bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Active</span>
                    <span className="text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Meta Ads</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">All months</div>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                After the Mid Market–only campaigns underperformed, strategy was updated to run combined campaigns targeting both SMB and Mid Market audiences together. Currently active — Mid Market lists are retargeted as custom audiences alongside SMB.
              </p>
            </div>
            <ManualActivitiesSection segment="mid_market" />
          </Panel>
        </div>
      )}

      {/* Channels — Email + Manual */}
      {activeSegment === "channels" && (
        <div className="space-y-4">
          <Panel
            title={<span className="inline-flex items-center gap-2"><Mail className="h-4 w-4" style={{color: coquette ? '#f472b6' : '#4f46e5'}} />Email Outreach — Instantly.ai</span>}
            subtitle="ISO / Channel partner email campaigns · lifetime analytics"
          >
            <EmailCampaignsPanel segment="channels" />
          </Panel>

          <Panel title={coquette ? "🍓 Other Channel Activities" : "Other Channel Activities"}>
            <ManualActivitiesSection segment="channels" />
          </Panel>
        </div>
      )}
    </div>
  );
}
